import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import * as api from '../lib/api';
import { EVENT_KINDS, kindOf, eventDayKey, formatPrice, formatEventTime } from '../lib/eventsFormat';

// Sirati events open in November 2026, so the calendar never goes back before it.
const FIRST_MONTH = { year: 2026, month: 10 };
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAY_MS = 86400000;

const dateKey = (d) => d.toISOString().slice(0, 10);
const keyToDate = (key) => new Date(`${key}T00:00:00Z`);
const longDay = (key) => keyToDate(key).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' });
const isBeforeFirst = (y, m) => y < FIRST_MONTH.year || (y === FIRST_MONTH.year && m < FIRST_MONTH.month);

const initialMonth = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  return isBeforeFirst(y, m) ? { ...FIRST_MONTH } : { year: y, month: m };
};

// Monday-first grid of whole weeks that contains the month.
const buildGrid = ({ year, month }) => {
  const first = new Date(Date.UTC(year, month, 1));
  const offset = (first.getUTCDay() + 6) % 7;
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const total = Math.ceil((offset + daysInMonth) / 7) * 7;
  const start = new Date(Date.UTC(year, month, 1 - offset));
  return Array.from({ length: total }, (_, i) => {
    const d = new Date(start.getTime() + i * DAY_MS);
    return { key: dateKey(d), day: d.getUTCDate(), inMonth: d.getUTCMonth() === month, weekend: i % 7 >= 5 };
  });
};

const CalendarPage = ({ onNavigate }) => {
  const [view, setView] = useState(initialMonth);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ in_person: true, online: true, course_launch: true });
  const [selected, setSelected] = useState(null);
  const cellRefs = useRef({});
  const todayKey = useMemo(() => new Date().toLocaleDateString('en-CA'), []);

  const grid = useMemo(() => buildGrid(view), [view]);
  const monthLabel = new Date(Date.UTC(view.year, view.month, 1))
    .toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' });

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    api.getEvents(grid[0].key, grid[grid.length - 1].key)
      .then((res) => { if (active) setEvents(res.data || []); })
      .catch(() => { if (active) { setEvents([]); setError('We could not load events right now. Please try again soon.'); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [grid]);

  const byDay = useMemo(() => {
    const map = {};
    events.filter((ev) => filters[ev.event_type] !== false).forEach((ev) => {
      (map[eventDayKey(ev)] = map[eventDayKey(ev)] || []).push(ev);
    });
    return map;
  }, [events, filters]);

  // After the month or events change, keep a valid selected day: today, else the first event day, else the 1st
  useEffect(() => {
    const inGrid = (k) => grid.some((c) => c.inMonth && c.key === k);
    setSelected((cur) => {
      if (cur && inGrid(cur)) return cur;
      if (inGrid(todayKey)) return todayKey;
      const firstWithEvent = grid.find((c) => c.inMonth && byDay[c.key]?.length);
      return (firstWithEvent || grid.find((c) => c.inMonth)).key;
    });
  }, [grid, byDay, todayKey]);

  const goMonth = (delta) => {
    const d = new Date(Date.UTC(view.year, view.month + delta, 1));
    const next = { year: d.getUTCFullYear(), month: d.getUTCMonth() };
    if (isBeforeFirst(next.year, next.month)) return;
    setSelected(null);
    setView(next);
  };
  const goToday = () => { setSelected(null); setView(initialMonth()); };
  const atFirst = view.year === FIRST_MONTH.year && view.month === FIRST_MONTH.month;

  const moveSelection = useCallback((key, deltaDays) => {
    const target = dateKey(new Date(keyToDate(key).getTime() + deltaDays * DAY_MS));
    const cell = grid.find((c) => c.key === target);
    if (!cell) return;
    if (!cell.inMonth) {
      const d = keyToDate(target);
      const next = { year: d.getUTCFullYear(), month: d.getUTCMonth() };
      if (isBeforeFirst(next.year, next.month)) return;
      setView(next);
    }
    setSelected(target);
    requestAnimationFrame(() => cellRefs.current[target]?.focus());
  }, [grid]);

  const onGridKeyDown = (e, key) => {
    const delta = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }[e.key];
    if (delta === undefined) return;
    e.preventDefault();
    moveSelection(key, delta);
  };

  const toggleFilter = (kind) => setFilters((f) => ({ ...f, [kind]: !f[kind] }));
  const dayEvents = selected ? (byDay[selected] || []) : [];
  const monthHasEvents = grid.some((c) => c.inMonth && byDay[c.key]?.length);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-[1200px] mx-auto p-4 lg:p-8 pb-28"
      data-testid="calendar-page"
    >
      <div className="flex items-center gap-3 flex-wrap mb-5">
        <button
          onClick={() => goMonth(-1)}
          disabled={atFirst}
          aria-label="Previous month"
          className="min-h-[44px] min-w-[44px] rounded-full bg-white/60 dark:bg-white/10 flex items-center justify-center text-primary hover:bg-teal/15 disabled:opacity-40 disabled:pointer-events-none"
          data-testid="calendar-prev"
        >
          <ChevronLeft className="w-5 h-5" aria-hidden="true" />
        </button>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-primary" aria-live="polite" data-testid="calendar-month">
          {monthLabel} <span className="text-lavender-dark font-medium">{view.year}</span>
        </h1>
        <button
          onClick={() => goMonth(1)}
          aria-label="Next month"
          className="min-h-[44px] min-w-[44px] rounded-full bg-white/60 dark:bg-white/10 flex items-center justify-center text-primary hover:bg-teal/15"
          data-testid="calendar-next"
        >
          <ChevronRight className="w-5 h-5" aria-hidden="true" />
        </button>
        <button
          onClick={goToday}
          className="min-h-[44px] px-4 rounded-full bg-white/60 dark:bg-white/10 text-sm font-semibold text-text-main hover:bg-teal/15"
        >
          Today
        </button>
        <div className="flex gap-2 flex-wrap lg:ml-auto" role="group" aria-label="Filter events by type">
          {Object.entries(EVENT_KINDS).map(([kind, meta]) => (
            <button
              key={kind}
              onClick={() => toggleFilter(kind)}
              aria-pressed={filters[kind]}
              className={`min-h-[44px] px-4 rounded-full border text-sm font-semibold flex items-center gap-2 transition-colors ${
                filters[kind] ? 'bg-card border-primary text-text-main' : 'bg-white/40 dark:bg-white/5 border-teal/20 text-text-muted'
              }`}
              data-testid={`calendar-filter-${kind}`}
            >
              <span className={`w-2.5 h-2.5 rounded-full ${meta.dot}`} aria-hidden="true" />
              {meta.short}
            </button>
          ))}
        </div>
      </div>

      {error && <p className="text-sm text-red-500 mb-3" role="alert">{error}</p>}

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-5 items-start">
        <div>
          <div className="glass-card rounded-arch-xl p-3 sm:p-5">
            <div className="grid grid-cols-7 gap-1 sm:gap-1.5 mb-2" aria-hidden="true">
              {WEEKDAYS.map((d, i) => (
                <div key={d} className={`text-center text-[11px] sm:text-xs font-bold uppercase tracking-wider py-1 ${i >= 5 ? 'text-lavender-dark dark:text-lavender-light' : 'text-text-muted'}`}>
                  {d}
                </div>
              ))}
            </div>
            <div
              role="grid"
              aria-label={`${monthLabel} ${view.year}`}
              className={`grid grid-cols-7 gap-1 sm:gap-1.5 ${loading ? 'opacity-60' : ''}`}
              data-testid="calendar-grid"
            >
              {grid.map((cell) => {
                const evs = byDay[cell.key] || [];
                const isSel = cell.key === selected;
                const isToday = cell.key === todayKey;
                return (
                  <button
                    key={cell.key}
                    ref={(el) => { cellRefs.current[cell.key] = el; }}
                    role="gridcell"
                    tabIndex={isSel ? 0 : -1}
                    aria-selected={isSel}
                    aria-label={`${longDay(cell.key)}, ${evs.length ? evs.map((e) => e.title).join(', ') : 'no events'}`}
                    onClick={() => setSelected(cell.key)}
                    onKeyDown={(e) => onGridKeyDown(e, cell.key)}
                    className={`relative flex flex-col items-center sm:items-start gap-1 text-left min-h-[58px] sm:min-h-[116px] p-1.5 sm:p-2 border transition-all overflow-hidden ${
                      isSel
                        ? 'bg-card border-primary shadow-lg rounded-t-[999px] rounded-b-[1rem] sm:rounded-b-[1.25rem]'
                        : `border-transparent rounded-[1rem] sm:rounded-[1.25rem] hover:border-teal/50 ${cell.weekend ? 'bg-lavender/10' : 'bg-white/60 dark:bg-white/5'}`
                    } ${cell.inMonth ? '' : 'opacity-40'}`}
                    data-testid={`calendar-day-${cell.key}`}
                  >
                    <span className={`font-heading font-semibold text-[13px] sm:text-sm w-6 h-6 flex items-center justify-center rounded-full ${isToday ? 'bg-primary text-white' : 'text-text-main'}`}>
                      {cell.day}
                    </span>
                    <span className="hidden sm:flex flex-col gap-1 w-full">
                      {evs.slice(0, 2).map((ev) => {
                        const kind = kindOf(ev);
                        return (
                          <span key={ev.id} className={`flex items-start gap-1 text-[11.5px] font-bold leading-tight px-2 py-1 rounded-xl ${kind.chip}`}>
                            <kind.Icon className="w-3 h-3 mt-0.5 shrink-0" aria-hidden="true" />
                            <span className="line-clamp-2 break-words">{ev.title}</span>
                          </span>
                        );
                      })}
                      {evs.length > 2 && <span className="text-[11px] font-bold text-text-muted pl-1">+{evs.length - 2} more</span>}
                    </span>
                    {evs.length > 0 && (
                      <span className="flex sm:hidden gap-0.5 mt-auto" aria-hidden="true">
                        {evs.slice(0, 3).map((ev) => <span key={ev.id} className={`w-2 h-2 rounded-full ${kindOf(ev).dot}`} />)}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex gap-x-5 gap-y-2 flex-wrap mt-4 text-[13px] text-text-muted">
            {Object.values(EVENT_KINDS).map((k) => (
              <span key={k.label} className="inline-flex items-center gap-2"><span className={`w-2.5 h-2.5 rounded-full ${k.dot}`} aria-hidden="true" />{k.label}</span>
            ))}
            <span>Weekends are shaded</span>
          </div>
        </div>

        <aside className="glass-card rounded-arch-xl p-5 sm:p-6 lg:sticky lg:top-24" aria-live="polite" data-testid="calendar-agenda">
          {selected && (
            <>
              <h2 className="font-heading text-xl font-semibold text-text-main">{longDay(selected)}</h2>
              <p className="text-sm text-text-muted">{dayEvents.length ? `${dayEvents.length} event${dayEvents.length > 1 ? 's' : ''}` : ''}</p>
            </>
          )}
          {dayEvents.map((ev) => {
            const kind = kindOf(ev);
            return (
              <button
                key={ev.id}
                onClick={() => onNavigate(`event:${ev.id}`)}
                className="w-full flex gap-3 text-left p-3.5 mt-3 rounded-arch bg-white/60 dark:bg-white/5 border border-teal/15 card-hover"
                data-testid={`calendar-event-${ev.id}`}
              >
                <span className={`w-11 h-11 rounded-2xl flex items-center justify-center text-white shrink-0 ${kind.tile}`}>
                  <kind.Icon className="w-5 h-5" aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-heading font-semibold text-[15px] leading-snug text-text-main">{ev.title}</span>
                  <span className="block text-[13px] text-text-muted mt-0.5">
                    {formatEventTime(ev)}{ev.city ? ` · ${ev.city}` : ''}
                  </span>
                  {ev.spots_left !== null && ev.spots_left !== undefined && ev.is_ticketed && (
                    <span className="block text-[12.5px] text-text-muted mt-0.5">{ev.spots_left === 0 ? 'Sold out' : ev.separate_by_gender ? (ev.spots_left <= 3 ? 'Almost sold out' : 'Limited spots') : `${ev.spots_left} spots left`}</span>
                  )}
                </span>
                <span className={`self-start text-xs font-bold px-3 py-1 rounded-full ${kind.pill}`}>
                  {ev.is_ticketed ? formatPrice(ev.your_price_cents) : 'Free'}
                </span>
              </button>
            );
          })}
          {selected && dayEvents.length === 0 && (
            <div className="text-center text-sm text-text-muted pt-6 pb-2">
              <CalendarDays className="w-8 h-8 mx-auto mb-2 text-teal/40" aria-hidden="true" />
              {!loading && !monthHasEvents
                ? 'No events are scheduled this month yet. Check back soon.'
                : 'Nothing scheduled this day.'}
            </div>
          )}
        </aside>
      </div>
    </motion.div>
  );
};

export default CalendarPage;
